import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { listTeams, createTeam, deleteTeam, clearAdminPassword } from '../utils/api';

export default function TeamsPage() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await listTeams();
      setTeams(data.teams);
    } catch (err) {
      if (err.status === 401) {
        clearAdminPassword();
        navigate('/');
        return;
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleCreated(team) {
    setTeams((prev) => [team, ...prev]);
    setShowForm(false);
  }

  async function handleDeleteTeam(team) {
    try {
      await deleteTeam(team.id);
      setTeams(prev => prev.filter(t => t.id !== team.id));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  }

  return (
    <Layout title="Teams">
      <style>{css}</style>

      <div className="page-head">
        <h1 className="h1">Teams</h1>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn-primary-sm">+ New team</button>
        )}
      </div>

      {showForm && (
        <NewTeamForm onCreated={handleCreated} onCancel={() => setShowForm(false)} />
      )}

      {loading && <div className="card"><p className="muted">Loading...</p></div>}
      {error && <div className="card" style={{ borderLeft: '4px solid #d70015' }}><p>{error}</p></div>}

      {!loading && !error && teams.length === 0 && !showForm && (
        <div className="card">
          <p className="muted">No teams yet. Click "+ New team" to create one.</p>
        </div>
      )}

      {!loading && teams.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table className="teams-table">
              <thead>
                <tr>
                  <th>Team</th>
                  <th>Access Code</th>
                  <th>Primary Contact</th>
                  <th>Users</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {teams.map((t) => (
                  <tr key={t.id}>
                    <td className="cell-name">{t.name}</td>
                    <td><code className="code-pill">{t.access_code}</code></td>
                    <td>
                      {(t.primary_contact_first_name || t.primary_contact_last_name)
                        ? `${t.primary_contact_first_name || ''} ${t.primary_contact_last_name || ''}`.trim()
                        : <span className="muted">—</span>}
                      {t.primary_contact_email && <div className="cell-sub">{t.primary_contact_email}</div>}
                    </td>
                    <td>{t.user_count}</td>
                    <td style={{display:'flex',gap:8,alignItems:'center'}}>
                      <Link to={`/teams/${t.id}`} className="row-link">View →</Link>
                      <button className="btn-delete-sm" onClick={() => handleDeleteTeam(t)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="teams-mobile">
            {teams.map((t) => (
              <Link key={t.id} to={`/teams/${t.id}`} className="team-mobile-card">
                <div className="team-mobile-name">{t.name}</div>
                <div className="team-mobile-row">
                  <span className="muted">Code</span>
                  <code className="code-pill">{t.access_code}</code>
                </div>
                <div className="team-mobile-row">
                  <span className="muted">Users</span>
                  <span>{t.user_count}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}

function NewTeamForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    name: '',
    access_code: '',
    primary_contact_first_name: '',
    primary_contact_last_name: '',
    primary_contact_email: '',
    primary_contact_phone: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await createTeam(form);
      onCreated({ ...result.team, user_count: 0 });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card">
      <h2 className="card-title">New team</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <Field label="Team name *" value={form.name} onChange={(v) => update('name', v)} placeholder="Boston Bruins Youth" />
          <Field label="Access code *" value={form.access_code} onChange={(v) => update('access_code', v.toUpperCase())} placeholder="BRUINS26" mono />
          <Field label="Primary contact first name" value={form.primary_contact_first_name} onChange={(v) => update('primary_contact_first_name', v)} />
          <Field label="Primary contact last name" value={form.primary_contact_last_name} onChange={(v) => update('primary_contact_last_name', v)} />
          <Field label="Primary contact email" value={form.primary_contact_email} onChange={(v) => update('primary_contact_email', v)} type="email" />
          <Field label="Primary contact phone" value={form.primary_contact_phone} onChange={(v) => update('primary_contact_phone', v)} />
        </div>

        {error && <p className="form-error">{error}</p>}

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={submitting || !form.name || !form.access_code} className="btn-primary">
            {submitting ? 'Creating...' : 'Create team'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', mono = false }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder || ''}
        onChange={(e) => onChange(e.target.value)}
        className={mono ? 'field-input field-input-mono' : 'field-input'}
      />
    </label>
  );
}

const css = `
  .page-head {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 20px; gap: 12px; flex-wrap: wrap;
  }
  .h1 { margin: 0; font-size: 28px; font-weight: 600; color: #1d1d1f; }
  .btn-primary-sm {
    background: #0071e3; color: #fff; padding: 8px 16px;
    border-radius: 12px; font-size: 14px; font-weight: 500;
    text-decoration: none; border: none; cursor: pointer; font-family: inherit;
  }
  .card {
    background: #fff; border-radius: 20px;
    box-shadow: 0 2px 20px rgba(0,0,0,0.06);
    padding: 24px; margin-bottom: 20px;
  }
  .card-title { margin: 0 0 16px 0; font-size: 18px; font-weight: 600; color: #1d1d1f; }
  .muted { color: #86868b; font-size: 14px; }
  .table-wrap { width: 100%; overflow-x: auto; }
  .teams-table { width: 100%; border-collapse: collapse; font-size: 14px; }
  .teams-table th {
    text-align: left; padding: 14px 20px; color: #86868b;
    font-weight: 500; font-size: 12px; text-transform: uppercase;
    border-bottom: 1px solid #f0f0f2;
  }
  .teams-table td {
    padding: 14px 20px; color: #1d1d1f; border-bottom: 1px solid #f5f5f7;
    vertical-align: top;
  }
  .teams-table tr:last-child td { border-bottom: none; }
  .cell-name { font-weight: 500; }
  .cell-sub { color: #86868b; font-size: 13px; margin-top: 2px; }
  .code-pill {
    background: #f5f5f7; padding: 4px 10px; border-radius: 6px;
    font-family: ui-monospace, monospace; font-size: 13px; color: #1d1d1f;
  }
  .btn-delete-sm { background: none; border: 1.5px solid #e0e0e5; border-radius: 8px; padding: 4px 10px; font-size: 12px; font-weight: 600; color: #c0392b; cursor: pointer; font-family: inherit; transition: all 0.12s; }
  .btn-delete-sm:hover { background: #fce4ec; border-color: #c0392b; }
  .row-link { color: #0071e3; text-decoration: none; font-weight: 500; }

  .teams-mobile { display: none; }
  .team-mobile-card {
    display: block; padding: 16px; border-bottom: 1px solid #f5f5f7;
    text-decoration: none; color: inherit;
  }
  .team-mobile-card:last-child { border-bottom: none; }
  .team-mobile-name { font-weight: 600; color: #1d1d1f; margin-bottom: 8px; }
  .team-mobile-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 4px 0; font-size: 14px;
  }

  .form-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px;
  }
  .field { display: flex; flex-direction: column; gap: 6px; }
  .field-label { font-size: 13px; color: #86868b; font-weight: 500; }
  .field-input {
    padding: 10px 14px; border-radius: 10px; border: 1px solid #d2d2d7;
    font-size: 15px; font-family: inherit; outline: none; box-sizing: border-box; width: 100%;
  }
  .field-input:focus { border-color: #0071e3; }
  .field-input-mono { font-family: ui-monospace, monospace; letter-spacing: 0.5px; }
  .form-error { color: #d70015; font-size: 14px; margin: 0 0 12px 0; }
  .form-actions { display: flex; gap: 12px; justify-content: flex-end; }
  .btn-primary {
    padding: 10px 20px; border-radius: 12px; border: none;
    background: #0071e3; color: #fff; font-size: 15px;
    font-weight: 500; font-family: inherit; cursor: pointer;
  }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-secondary {
    padding: 10px 20px; border-radius: 12px;
    background: transparent; border: 1px solid #d2d2d7;
    color: #1d1d1f; font-size: 15px; font-family: inherit; cursor: pointer;
  }

  @media (max-width: 700px) {
    .h1 { font-size: 22px; }
    .card { padding: 16px; border-radius: 16px; }
    .table-wrap { display: none; }
    .teams-mobile { display: block; }
    .form-grid { grid-template-columns: 1fr; }
  }
`;
